/** @jsxRuntime automatic */
/** @jsxImportSource react */
import React from "react"
import { TbArrowBackUp, TbArrowForwardUp, TbCopy, TbTrash, TbPlus, TbChevronLeft, TbChevronRight, TbDots, TbLayoutGrid, TbFileExport, TbMaximize, TbCursorText } from "react-icons/tb"
import { REPORT_TEMPLATES, reportTemplateLayout } from "./report-template-layout"

// Presentation only: all actions operate on the existing dashboard controller.
export function ReportCanvasToolbar({ labels, editor }) {
  const l = labels.designer
  const menuRef = React.useRef(null)
  React.useEffect(() => {
    const menu = menuRef.current
    if (!menu) return
    const document = menu.ownerDocument
    const closeOutside = (event) => {
      if (menu.open && !menu.contains(event.target)) menu.open = false
    }
    const closeOnEscape = (event) => {
      if (!menu.open || event.key !== "Escape") return
      event.preventDefault()
      event.stopPropagation()
      menu.open = false
      menu.querySelector("summary")?.focus({ preventScroll: true })
    }
    document.addEventListener("pointerdown", closeOutside, true)
    document.addEventListener("focusin", closeOutside, true)
    document.addEventListener("keydown", closeOnEscape, true)
    return () => {
      document.removeEventListener("pointerdown", closeOutside, true)
      document.removeEventListener("focusin", closeOutside, true)
      document.removeEventListener("keydown", closeOnEscape, true)
    }
  }, [])
  const action = (callback) => (event) => {
    event.currentTarget.closest("details")?.removeAttribute("open")
    callback()
  }
  return <div className="designer-contextbar">
    <button type="button" className="designer-icon-button" title={l.undo} aria-label={l.undo} disabled={editor.locked || !editor.canUndo} onClick={editor.undo}><TbArrowBackUp /></button>
    <button type="button" className="designer-icon-button" title={l.redo} aria-label={l.redo} disabled={editor.locked || !editor.canRedo} onClick={editor.redo}><TbArrowForwardUp /></button>
    <span className="designer-divider" />
    <input className="designer-page-name" aria-label={l.renamePage} value={editor.page.name || ""} disabled={editor.locked} onChange={(event) => editor.renamePage(event.target.value)} />
    {editor.selected ? <><span className="designer-divider" /><span className="designer-selection-name">{editor.selectedCount > 1 ? l.multipleSelection : editor.selected.title}</span>
      <button type="button" disabled={editor.locked || editor.selectedCount > 1} onClick={() => editor.editWidget("data")}>{labels.data}</button>
      <button type="button" disabled={editor.locked || editor.selectedCount > 1} onClick={() => editor.editWidget("design")}>{labels.design}</button>
      <button type="button" className="designer-icon-button" aria-label={editor.selectedCount > 1 ? l.duplicateSelection : labels.duplicateWidget} title={editor.selectedCount > 1 ? l.duplicateSelection : labels.duplicateWidget} disabled={editor.locked} onClick={editor.duplicateWidget}><TbCopy /></button>
      <button type="button" className="designer-icon-button" aria-label={editor.selectedCount > 1 ? l.deleteSelection : labels.deleteWidget} title={editor.selectedCount > 1 ? l.deleteSelection : labels.deleteWidget} disabled={editor.locked} onClick={editor.deleteWidget}><TbTrash /></button>
      <button type="button" className="designer-icon-button" aria-label={labels.exportTable} title={labels.exportTable} disabled={editor.selectedCount > 1 || !editor.selected.data?.dataset_id} onClick={editor.exportTable}><TbFileExport /></button>
      <button type="button" className="designer-icon-button" aria-label={labels.fullView} title={labels.fullView} disabled={editor.selectedCount > 1} onClick={editor.fullView}><TbMaximize /></button>
    </> : null}
    <button type="button" disabled={editor.locked || !editor.selectedCount} onClick={editor.copyWidgets}>{l.copySelection}</button>
    <button type="button" disabled={editor.locked || !editor.canPaste} title={l.pasteScope} onClick={editor.pasteWidgets}>{l.pasteSelection}</button>
    <div className="designer-contextbar-end">
      {editor.preview ? (editor.hasFilters ? <span>{l.previewHint}</span> : null) : <>
        <button type="button" className="designer-icon-button designer-orientation-button" aria-label={labels.portrait} title={labels.portrait} aria-pressed={editor.portrait} disabled={editor.locked} onClick={() => editor.orientation("portrait")}><span aria-hidden="true">▯</span></button>
        <button type="button" className="designer-icon-button designer-orientation-button" aria-label={labels.landscape} title={labels.landscape} aria-pressed={!editor.portrait} disabled={editor.locked} onClick={() => editor.orientation("landscape")}><span aria-hidden="true">▭</span></button>
        <button type="button" className="designer-icon-button" aria-label={l.tidyLayout} title={l.tidyLayout} disabled={editor.locked} onClick={editor.tidy}><TbLayoutGrid /></button>
        <label className="designer-drag-toggle"><input type="checkbox" checked={editor.dragEnabled} disabled={editor.locked} onChange={(event) => editor.setDrag(event.target.checked)} />{labels.drag}</label>
        <label className="designer-drag-toggle" title={l.canvasShortcuts}><input type="checkbox" checked={editor.snapEnabled} disabled={editor.locked} onChange={(event) => editor.setSnapEnabled(event.target.checked)} />{l.snapToGuides}</label>
      </>}
      <details ref={menuRef} className="designer-canvas-menu"><summary aria-label={labels.more} title={labels.more}><TbDots /></summary><div>
        <button type="button" disabled={editor.locked} onClick={action(editor.selectAll)}>{l.selectAllWidgets}</button>
        <button type="button" disabled={editor.locked} onClick={action(editor.renameDashboard)}>{labels.renameDashboard}</button>
        <button type="button" disabled={editor.readOnly || editor.saving} onClick={action(editor.exportPdf)}>{labels.exportPdf}</button>
        <button type="button" disabled={editor.readOnly || editor.saving} onClick={action(editor.publish)}>{editor.published ? labels.unpublishDashboard : labels.publishDashboard}</button>
        <button type="button" disabled={editor.readOnly || !editor.published || editor.saving} onClick={action(editor.share)}>{labels.shareAccess}</button>
      </div></details>
    </div>
  </div>
}

export function ReportPageStrip({ labels, editor }) {
  const l = labels.designer
  const [menu, setMenu] = React.useState(null)
  const menuRef = React.useRef(null)
  const menuID = React.useId()
  const viewOnly = editor.preview || editor.readOnly
  React.useEffect(() => { setMenu(null) }, [editor.page.id, editor.locked, viewOnly])
  React.useEffect(() => {
    if (!menu) return
    const document = menu.button.ownerDocument
    const closeOutside = (event) => {
      if (menuRef.current?.contains(event.target) || menu.button.contains(event.target)) return
      setMenu(null)
    }
    const close = () => setMenu(null)
    const escape = (event) => {
      if (event.key !== "Escape") return
      event.preventDefault()
      event.stopPropagation()
      setMenu(null)
      menu.button.focus({ preventScroll: true })
    }
    menuRef.current?.querySelector("button:not(:disabled)")?.focus({ preventScroll: true })
    document.addEventListener("pointerdown", closeOutside, true)
    document.addEventListener("focusin", closeOutside, true)
    document.addEventListener("keydown", escape, true)
    document.addEventListener("scroll", closeOutside, true)
    document.defaultView?.addEventListener("resize", close)
    return () => {
      document.removeEventListener("pointerdown", closeOutside, true)
      document.removeEventListener("focusin", closeOutside, true)
      document.removeEventListener("keydown", escape, true)
      document.removeEventListener("scroll", closeOutside, true)
      document.defaultView?.removeEventListener("resize", close)
    }
  }, [menu])
  const openMenu = (page, button) => {
    if (menu) return setMenu(null)
    const rect = button.getBoundingClientRect()
    // Open above the footer and outside the scrolling tab row.
    const view = button.ownerDocument.defaultView
    setMenu({ page, button, left: Math.max(8, Math.min(rect.left, view.innerWidth - 208)), bottom: view.innerHeight - rect.top + 4 })
  }
  const run = (callback) => () => {
    if (editor.locked) return
    setMenu(null)
    menu.button.focus({ preventScroll: true })
    callback(menu.page)
  }
  const navigateMenu = (event) => {
    if (event.key === "Tab") { setMenu(null); return }
    if (!["ArrowDown", "ArrowUp", "Home", "End"].includes(event.key)) return
    event.preventDefault()
    event.stopPropagation()
    const buttons = [...event.currentTarget.querySelectorAll("button:not(:disabled)")]
    const current = buttons.indexOf(event.target)
    const next = event.key === "Home" ? 0 : event.key === "End" ? buttons.length - 1 : (current + (event.key === "ArrowDown" ? 1 : -1) + buttons.length) % buttons.length
    buttons[next]?.focus({ preventScroll: true })
  }
  return <footer className="designer-canvas-footer">
    <div className="designer-page-strip" aria-label={l.pages}>{editor.pages.map((page, index) => {
      const current = page.id === editor.page.id
      return <React.Fragment key={page.id}>
        <button type="button" className={current ? "active" : ""} title={page.name} aria-current={current ? "page" : undefined} onClick={() => editor.selectPage(page.id)} onDoubleClick={() => { if (!editor.locked) editor.renamePageDialog(page) }}><span className="designer-page-thumb">{index + 1}</span><span>{page.name}</span></button>
        {current && !viewOnly ? <button type="button" className="designer-page-more" aria-label={l.pageActions} title={l.pageActions} aria-haspopup="menu" aria-controls={menu ? menuID : undefined} aria-expanded={!!menu} disabled={editor.locked} onClick={(event) => openMenu(page, event.currentTarget)}><TbDots /></button> : null}
      </React.Fragment>
    })}{viewOnly ? null : <button type="button" className="designer-add-page" aria-label={l.addPage} title={l.addPage} disabled={editor.locked} onClick={editor.addPage}><TbPlus /></button>}</div>
    {menu && !viewOnly && !editor.locked ? <div ref={menuRef} id={menuID} className="designer-page-menu" role="menu" aria-label={menu.page.name} style={{ left: menu.left, bottom: menu.bottom }} onKeyDown={navigateMenu}>
      <button type="button" role="menuitem" tabIndex={-1} onClick={run(editor.renamePageDialog)}><TbCursorText aria-hidden="true" />{l.renamePageAction}</button>
      <button type="button" role="menuitem" tabIndex={-1} onClick={run(editor.duplicatePage)}><TbCopy aria-hidden="true" />{l.duplicatePage}</button>
      <hr />
      <button type="button" role="menuitem" tabIndex={-1} className="designer-page-menu-danger" disabled={editor.pages.length <= 1} onClick={run(editor.deletePage)}><TbTrash aria-hidden="true" />{l.deletePage}</button>
    </div> : null}
    <div className="designer-page-controls">
      <span className="designer-widget-count">{labels.widgetCount.replace("{count}", String(editor.page.widgets.length))}</span>
      {editor.selectedCount ? <output className="designer-widget-count" aria-label={l.selectedWidgets} title={l.selectedWidgets}>{editor.selectedCount}</output> : null}
      {viewOnly ? null : <><button type="button" className="designer-icon-button" aria-label={l.moveUp} title={l.moveUp} disabled={editor.locked || editor.pages.indexOf(editor.page) === 0} onClick={() => editor.movePage(-1)}><TbChevronLeft /></button>
      <button type="button" className="designer-icon-button" aria-label={l.moveDown} title={l.moveDown} disabled={editor.locked || editor.pages.indexOf(editor.page) === editor.pages.length - 1} onClick={() => editor.movePage(1)}><TbChevronRight /></button></>}
      <button type="button" className="designer-fit" onClick={() => editor.setZoom(0)}>{l.fit}</button>
      <input aria-label={l.zoom} type="range" min="15" max="150" value={Math.round(editor.scale * 100)} onChange={(event) => editor.setZoom(Number(event.target.value) / 100)} /><span>{Math.round(editor.scale * 100)}%</span>
    </div>
  </footer>
}

export function ReportTemplatePanel({ labels, onAdd, onClose, disabled, canvas }) {
  const l = labels.designer
  return <aside className="datox-react-dashboard__sidebar designer-template-panel"><div className="datox-react-dashboard__panel-header"><h3>{l.templates}</h3><button type="button" onClick={onClose} aria-label={l.closePanel}>×</button></div><p className="designer-empty-note">{l.templateHint}</p><div className="designer-layouts">{REPORT_TEMPLATES.map((layout) => {
    const name = l[`layout${layout[0].toUpperCase()}${layout.slice(1)}`]
    return <figure key={layout} data-template={layout} className="designer-layout-card">
      <TemplatePaper widgets={reportTemplateLayout(layout, canvas)} page={canvas} />
      <figcaption><strong>{name}</strong><button type="button" disabled={disabled} aria-label={l.addTemplate.replace("{name}", name)} onClick={() => onAdd(layout)}><TbPlus />{l.addPage}</button></figcaption>
    </figure>
  })}</div></aside>
}

function TemplatePaper({ widgets, page }) {
  return <span className="designer-paper" aria-hidden="true"><span className="designer-paper-sheet" style={{ aspectRatio: `${page.width} / ${page.height}` }}>{widgets.map((widget, index) => <span key={index} className="designer-paper-widget" data-type={widget.type} style={{ left: `${widget.layout.x / page.width * 100}%`, top: `${widget.layout.y / page.height * 100}%`, width: `${widget.layout.w / page.width * 100}%`, height: `${widget.layout.h / page.height * 100}%` }}>{SKETCH[widget.type]}</span>)}</span></span>
}

const SKETCH = {
  header: <i />,
  kpi: <><i /><b /></>,
  line: <svg viewBox="0 0 100 40" preserveAspectRatio="none"><polyline points="0,32 14,26 28,29 42,18 57,21 71,11 85,14 100,5" /></svg>,
  bar: <><i /><i /><i /><i /><i /></>,
  pie: <i />,
  table: <><i /><i /><i /><i /><i /><i /></>,
}
